import { Form, Input, Button, Upload, Row, Col, Collapse, Select, message, FormInstance, Radio } from "antd"
import type { UploadChangeParam } from "antd/es/upload"
import { EyeOutlined, DeleteOutlined, UserOutlined, FileTextOutlined, CloudUploadOutlined, PaperClipOutlined } from "@ant-design/icons"
import React, { useEffect } from "react"
import { ISapAsset, ISapAssetDetail, ISapAssetDto, ISapImportValidationError } from "@/types"
import { useUserStore } from "@/stores/user/userStore"
import AssetPreviewCard from "./AssetPreviewCard"

const TYPE_OF_REQUEST_DESCRIPTIONS: Record<string, string> = {
  Create: "Request to create new master data",
  Extend: "Request to open for another branch",
  Change: "Request to update existing master data",
  Deactivate: "Request to deactivate master data for all units",
}

const NOTE_PLACEHOLDERS: Record<string, string> = {
  Create: "Example: New asset registration needed for purchased equipment or vehicle",
  Extend: "Example: Open existing asset for another branch or company code",
  Change: "Example: Update asset master data or classification details",
  Deactivate: "Example: Asset is retired and should be deactivated",
}
const ASSET_UNIT_OPTIONS = ["BRM", "SHS", "LMR", "CPM", "GMI"]

interface SapAssetFormProps {
  isEditMode: boolean
  isReadOnly?: boolean
  form: FormInstance<ISapAssetDto>
  file: File | null
  loading: boolean
  canSubmit: boolean
  onFileChange: (info: UploadChangeParam) => void
  onFinish: (values: ISapAssetDto) => void
  previewModalOpen: boolean
  previewLoading: boolean
  previewError: string | null
  previewItems: ISapAssetDetail[]
  previewIsValid: boolean
  previewTemplateKey?: string | null
  previewRequiredFields: string[]
  previewValidationErrors: ISapImportValidationError[]
  setPreviewModalOpen: (open: boolean) => void
  formData?: ISapAsset | null
}

const SapAssetForm: React.FC<SapAssetFormProps> = ({
  isEditMode,
  isReadOnly = false,
  form,
  file,
  loading,
  canSubmit,
  onFileChange,
  onFinish,
  previewModalOpen,
  previewLoading,
  previewError,
  previewItems,
  previewIsValid,
  previewTemplateKey = null,
  previewRequiredFields,
  previewValidationErrors,
  setPreviewModalOpen,
  formData,
}) => {
  const { userData } = useUserStore()
  const selectedTypeOfRequest = Form.useWatch("type_of_request", form)
  const notePlaceholder = NOTE_PLACEHOLDERS[selectedTypeOfRequest || ""] || "Example: Explain the business reason or important context for this request"

  useEffect(() => {
    if (!isEditMode && userData) {
      form.setFieldsValue({
        employee_id: userData.employee_id,
        fullname: userData.fullname,
        level: userData.job_level,
        department: userData.organization_name,
      })
    } else if (isEditMode && formData) {
      form.setFieldsValue({
        employee_id: formData.employee_id,
        fullname: formData.fullname,
        level: formData.level,
        department: formData.department,
        type_of_request: formData.type_of_request,
        note: formData.note,
      })
    }
  }, [isEditMode, userData, form, formData])

  const handleRemoveFile = () => {
    setPreviewModalOpen(false)
    onFileChange({ file: { status: "removed", uid: "", name: "" } } as UploadChangeParam)
  }

  return (
    <>
      <Form form={form} layout="vertical" onFinish={onFinish} style={{ maxWidth: 1000, margin: "0 auto" }}>
        <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
          <Collapse.Panel
            header={
              <>
                <UserOutlined /> User Information
              </>
            }
            key="1"
          >
            <Row gutter={24}>
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Employee ID" name="employee_id">
                  <Input disabled bordered={false} style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              </Col>
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Fullname" name="fullname">
                  <Input disabled bordered={false} style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Level" name="level">
                  <Input disabled bordered={false} style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              </Col>
              <Col span={12} xs={24} md={12}>
                <Form.Item label="Department" name="department">
                  <Input disabled bordered={false} style={{ backgroundColor: "#f5f5f5" }} />
                </Form.Item>
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>

        <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
          <Collapse.Panel
            header={
              <>
                <FileTextOutlined /> Request Details
              </>
            }
            key="1"
          >
            <div style={{ marginBottom: 16, padding: "12px 16px", background: "#faf7f0", border: "1px solid #f0d8a8", borderRadius: 8, color: "#8c6b1f" }}>
              Provide a clear request type and supporting note so approvers can review this request faster.
            </div>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item
                  label="Type of Request"
                  name="type_of_request"
                  rules={[{ required: true, message: "Please select type of request" }]}
                  extra={TYPE_OF_REQUEST_DESCRIPTIONS[selectedTypeOfRequest || ""] || "Select the request type that best matches the SAP master data action."}
                >
                  <Select placeholder="Select type of request" disabled={loading || isEditMode || isReadOnly} size="large" allowClear>
                    <Select.Option value="Create">Create</Select.Option>
                    {/* <Select.Option value="Extend">Open Unit</Select.Option> */}
                    {/* <Select.Option value="Change">Change</Select.Option> */}
                    {/* <Select.Option value="Deactivate">Deactivate</Select.Option> */}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            {!isEditMode && (
              <Row gutter={24}>
                <Col span={24}>
                  <Form.Item
                    label="Fix Asset For Unit"
                    name="branch"
                    rules={[{ required: true, message: "Please select fix asset unit" }]}
                    extra="Approval follows the selected unit. One request is only for one unit; if another asset belongs to another unit, create another request."
                  >
                    <Radio.Group disabled={loading || isReadOnly}>
                      {ASSET_UNIT_OPTIONS.map(unit => (
                        <Radio.Button key={unit} value={unit}>
                          {unit}
                        </Radio.Button>
                      ))}
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
            )}
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item label="Note" name="note" extra="Optional. Use this field to explain the business reason or any important context for approvers.">
                  <Input.TextArea placeholder={notePlaceholder} disabled={loading || isEditMode || isReadOnly} rows={4} />
                </Form.Item>
              </Col>
            </Row>
          </Collapse.Panel>
        </Collapse>

        <Collapse defaultActiveKey={["1"]} ghost style={{ marginBottom: 24, background: "#fff", borderRadius: 8 }}>
          <Collapse.Panel
            header={
              <>
                <CloudUploadOutlined /> Upload Main Document (Excel)
              </>
            }
            key="1"
          >
            <Form.Item name="file" rules={[{ required: true, message: "Please upload a file" }]}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {!file ? (
                  <Upload.Dragger
                    beforeUpload={uploadFile => {
                      const isExcel =
                        uploadFile.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || uploadFile.type === "application/vnd.ms-excel" || uploadFile.name.endsWith(".xls") || uploadFile.name.endsWith(".xlsx")
                      if (!isExcel) {
                        void message.error("You can only upload Excel file!")
                        return Upload.LIST_IGNORE
                      }
                      return false
                    }}
                    accept=".xls,.xlsx"
                    onChange={onFileChange}
                    fileList={[]}
                    maxCount={1}
                    showUploadList={false}
                    disabled={loading || isReadOnly}
                    style={{ padding: 32, background: "#fafafa", border: "1px dashed #d9d9d9", borderRadius: 8 }}
                  >
                    <p className="ant-upload-drag-icon">
                      <CloudUploadOutlined style={{ color: "#1890ff", fontSize: 48 }} />
                    </p>
                    <p className="ant-upload-text">Click or drag file to this area to upload</p>
                    <p className="ant-upload-hint">Support for a single upload. Only .xls and .xlsx files are allowed.</p>
                    <div onClick={e => e.stopPropagation()} style={{ marginTop: 8 }}>
                      <a href="https://bumiresourcesmineralstbk.sharepoint.com/:x:/s/brms-sap/IQDgwx7jTtsYQZ_n28e_nwX-AVUKzecqt04EOAESGcfdNzg?e=pdSRr4" target="_blank" rel="noopener noreferrer" style={{ color: "#1890ff" }}>
                        Download Template
                      </a>
                    </div>
                  </Upload.Dragger>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 16,
                      padding: 16,
                      background: "#f0faff",
                      border: "1px solid #bae7ff",
                      borderRadius: 8,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12, overflow: "hidden", flex: 1, minWidth: 200 }}>
                      <div
                        style={{
                          minWidth: 40,
                          width: 40,
                          height: 40,
                          background: "#1890ff",
                          borderRadius: 8,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <PaperClipOutlined style={{ fontSize: 20, color: "#fff" }} />
                      </div>
                      <div style={{ overflow: "hidden", flex: 1 }}>
                        <div style={{ fontWeight: 500, color: "#262626", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</div>
                        <div style={{ fontSize: 12, color: "#8c8c8c" }}>{(file.size / 1024).toFixed(2)} KB</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Button size="small" type="default" icon={<EyeOutlined />} onClick={() => setPreviewModalOpen(!previewModalOpen)} style={{ display: "flex", alignItems: "center" }}>
                        {previewModalOpen ? "Hide" : "Preview"}
                      </Button>
                      <Button size="small" danger type="default" icon={<DeleteOutlined />} onClick={handleRemoveFile} disabled={isReadOnly} style={{ display: "flex", alignItems: "center" }}>
                        Remove
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Form.Item>

            {(previewModalOpen || (isEditMode && formData?.detail && formData.detail.length > 0)) && (
              <div style={{ marginBottom: 24 }}>
                <AssetPreviewCard
                  items={previewItems}
                  loading={previewLoading}
                  error={previewError}
                  templateKey={previewTemplateKey || formData?.template_key}
                  requiredFields={previewRequiredFields}
                  validationErrors={previewIsValid ? [] : previewValidationErrors}
                />
              </div>
            )}
          </Collapse.Panel>
        </Collapse>

        <Form.Item>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
            <Button size="large" onClick={() => window.history.back()}>
              Cancel
            </Button>
            {!isReadOnly && (
              <Button type="primary" htmlType="submit" loading={loading} disabled={!canSubmit} size="large" style={{ minWidth: 120 }}>
                {isEditMode ? "Update Asset" : "Create Asset"}
              </Button>
            )}
          </div>
        </Form.Item>
      </Form>
    </>
  )
}

export default SapAssetForm
